import { parse, serialize, type CookieSerializeOptions } from 'cookie-es'
import type { IncomingMessage, ServerResponse } from 'node:http'

export type CookieValue = string | number | boolean | object | null | undefined

export type CookieStore = {
  get: <T = unknown>(
    key: string,
    options?: { fromRes?: boolean; parseJSON?: boolean },
  ) => T | string | undefined
  getAll: (options?: {
    fromRes?: boolean
    parseJSON?: boolean
  }) => Record<string, unknown>
  set: (
    key: string,
    value?: CookieValue,
    options?: CookieSerializeOptions,
  ) => void
  remove: (key: string, options?: CookieSerializeOptions) => void
}

type CookieStoreOptions = {
  req?: IncomingMessage
  res?: ServerResponse
}

const readRequestCookies = (req?: IncomingMessage) => {
  if (import.meta.client) {
    return document.cookie
  }

  return req?.headers.cookie ?? ''
}

const readResponseCookies = (res?: ServerResponse) => {
  const current = res?.getHeader('Set-Cookie')
  const setCookies = Array.isArray(current)
    ? current
    : current
      ? [String(current)]
      : []

  return setCookies.map((cookie) => cookie.split(';')[0]).join('; ')
}

const parseValue = (value: string | undefined, parseJSON: boolean) => {
  if (!parseJSON || value === undefined) {
    return value
  }

  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

const writeCookie = (cookie: string, res?: ServerResponse) => {
  if (import.meta.client) {
    document.cookie = cookie
    return
  }

  const current = res?.getHeader('Set-Cookie')
  const existing = Array.isArray(current)
    ? current
    : current
      ? [String(current)]
      : []
  res?.setHeader('Set-Cookie', [...existing, cookie])
}

export const createCookieStore = ({
  req,
  res,
}: CookieStoreOptions = {}): CookieStore => ({
  get(key, options = {}) {
    const cookies = parse(
      options.fromRes ? readResponseCookies(res) : readRequestCookies(req),
    )
    return parseValue(cookies[key], options.parseJSON ?? true)
  },
  getAll(options = {}) {
    const cookies = parse(
      options.fromRes ? readResponseCookies(res) : readRequestCookies(req),
    )

    return Object.fromEntries(
      Object.entries(cookies).map(([key, value]) => [
        key,
        parseValue(value as string | undefined, options.parseJSON ?? true),
      ]),
    )
  },
  set(key, value = '', options = { path: '/' }) {
    writeCookie(serialize(key, value, options), res)
  },
  remove(key, options = { path: '/' }) {
    writeCookie(serialize(key, '', { ...options, expires: new Date(0) }), res)
  },
})
